import { Box, HStack, Input, Stack, Text } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useRef } from 'react';
import { useGiftCardApply } from '../components/CheckoutGiftCard';
import { usePromoCodeApply } from '../components/CheckoutPromoCode';
import type { Product } from '../useCheckoutForm';
import { formatAmount } from './format';
import { Field } from './Section';

function AppliedChip({ label, detail, onRemove }: { label: string; detail?: string; onRemove: () => void }) {
  return (
    <Box className="cv2-applied">
      <Box minW={0}>
        <Text className="cv2-applied-label">{label}</Text>
        {detail && (
          <Text className="cv2-hint" mt={0.5}>
            {detail}
          </Text>
        )}
      </Box>
      <button type="button" className="cv2-btn-ghost" onClick={onRemove}>
        <CloseIcon boxSize="9px" /> Retirer
      </button>
    </Box>
  );
}

function CodeInput({ placeholder, isLoading, onApply, error, onFocus }: { placeholder: string; isLoading: boolean; onApply: (value: string | undefined) => void; error?: string; onFocus?: () => void }) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <HStack spacing={2.5} align="flex-start">
      <Field error={error} flex={1}>
        <Input
          className="cv2-input"
          variant="unstyled"
          ref={ref}
          placeholder={placeholder}
          autoCapitalize="characters"
          onFocus={onFocus}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              onApply(ref.current?.value);
            }
          }}
        />
      </Field>
      <button type="button" className="cv2-btn-apply" disabled={isLoading} onClick={() => onApply(ref.current?.value)}>
        {isLoading ? '…' : 'Appliquer'}
      </button>
    </HStack>
  );
}

function PromoCodeBlock({ product }: { product: Product }) {
  const promo = usePromoCodeApply(product);
  return (
    <Box>
      <Text className="cv2-label">Code promo</Text>
      {promo.appliedCode ? (
        <AppliedChip label={promo.appliedCode} detail={promo.discount ? `− ${formatAmount(promo.discount)} sur votre commande` : undefined} onRemove={promo.clear} />
      ) : (
        <CodeInput placeholder="Votre code" isLoading={promo.isLoading} onApply={promo.apply} error={promo.error as string | undefined} onFocus={() => promo.clearErrors('PromoCode')} />
      )}
    </Box>
  );
}

function GiftCardBlock() {
  const gift = useGiftCardApply();
  return (
    <Box>
      <Text className="cv2-label">Carte cadeau</Text>
      {gift.giftCardCode && gift.giftCardAmount ? (
        <AppliedChip label={`Carte ${gift.giftCardCode}`} detail={`Solde ${formatAmount(gift.giftCardAmount)} · il restera ${formatAmount(gift.remainingOnCard)} après paiement`} onRemove={gift.clear} />
      ) : (
        <CodeInput placeholder="Numéro de la carte" isLoading={gift.isLoading} onApply={gift.apply} />
      )}
    </Box>
  );
}

/**
 * Bloc « Avantages » : code promo et carte cadeau (packs uniquement). La
 * carte cadeau n'est proposée que si la config BO l'autorise pour ce
 * service — le serveur revérifie au paiement.
 */
export function Advantages({ product }: { product: Product }) {
  const giftCardAllowed =
    product.type === 'service' &&
    (!mondayMindbody?.giftCardConfig?.restrictToEligible || mondayMindbody.giftCardConfig.eligibleServiceIds.includes(String(product.Id)));

  return (
    <Stack spacing={5} divider={<Box borderBottomWidth="1px" borderColor="#e8e8e8" />} className="CheckoutV2Advantages">
      <PromoCodeBlock product={product} />
      {giftCardAllowed && <GiftCardBlock />}
    </Stack>
  );
}
